import React, { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, message, Popover, Tooltip } from 'antd';
import {
  AddDimensionBtn,
  AddDimensionWrapper,
  DimensionTitle,
  SelectWrapper,
  StyledSelect,
} from '../styled';
import { AddDimensionPopupType } from '../types';
import { BACKEND_NAME_SEPARATOR } from '../constants';
import {
  getFilteredOptions,
  getDimensionState,
  updateDimensionState,
  isFirstDimension,
  getInsertIndex,
  getNewDimensionCombination,
  getDimensionNameList,
} from '../utils';
import { useUpdateConfigList, useUpdateConfigListForMetric } from '../hooks/useUpdateConfigList';
import { useGlobalFeature } from '@src/globalStore/globalFeatureFlagsStore';
import { globalFeatureNames } from '@src/utils/featureToggle/constants';
import useCreateDisable from '@src/components/ai-cohorts/hooks/useCreateDisable';
import { useSelector } from 'react-redux';
import { isAdminRoleSelector } from '@src/store/common/selectors';
import { useAtomValue, useSetAtom } from 'jotai';
import { aiCohortDrawerAtom } from '@src/components/ai-alerts/HeaderPanel/SensitivityControl/atoms';
import { isConfigLoadingAtom } from '../atoms';
import DimensionSelector from './DimensionSelector';
import { isAppModule } from '@src/utils/constants';

export default function AddDimensionPopup({
  level,
  dimensionText,
  singleDimensionOptions,
  preLevelDimensionOptions,
  dimensionData,
  aiConfigList,
}: AddDimensionPopupType) {
  const isAdminUser = useSelector(isAdminRoleSelector);
  const [selectValArr, setSelectArrState] = useState(getDimensionState(level === 1 ? 1 : 2));
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const disableCreate = useCreateDisable(selectValArr, dimensionData);
  const msgText = isFirstDimension(level) ? `dimension` : 'dimension combination';
  const popupTitle = `Create ${dimensionText} Combination${level > 1 ? 's' : ''}`;

  const setIsConfigLoading = useSetAtom(isConfigLoadingAtom);

  const onDimensionAddSuccess = () => {
    message.open({
      content: `${msgText} added successfully`,
      type: 'success',
      style: { textTransform: 'capitalize' },
    });
    // Note: Don't set loading to false here, let the refetch handle it
  };

  const onDimensionAddError = () => {
    message.error(`Unable to add ${msgText}. Please try again.`);
    setIsConfigLoading(false);
  };

  const aiCohortDrawerConfig = useAtomValue(aiCohortDrawerAtom);
  const enableDPICustomDimension = useGlobalFeature(globalFeatureNames.DPI_CUSTOM_DIMENSION);

  // Always call both hooks to avoid conditional hook calls
  const generalMutation = useUpdateConfigList(onDimensionAddSuccess, onDimensionAddError);
  const metricMutation = useUpdateConfigListForMetric(
    onDimensionAddSuccess,
    onDimensionAddError,
    aiCohortDrawerConfig?.metrics || [],
  );

  // Choose which mutation to use based on feature flag
  const { mutate: mutateAiAlertsConfig } = enableDPICustomDimension
    ? metricMutation
    : generalMutation;

  const onBlur = () => {
    setSelectArrState(getDimensionState(level === 1 ? 1 : 2));
  };

  const handlePopoverChange = (isOpen: boolean): void => {
    if (!isAdminUser) {
      return;
    }
    setPopoverOpen(isOpen);
    onBlur();
  };

  const updateSelectValues = (valObj, index: number): void => {
    setSelectArrState((state) => updateDimensionState(state, index, valObj));
  };

  const onSelectChangeHandler = (index: number) => (_, valObj) => updateSelectValues(valObj, index);

  // Handler for DimensionSelector which expects just the dimension string
  const onDimensionSelectorChange = (index: number) => (newDimension: string) => {
    // Find the corresponding option object from singleDimensionOptions
    const selectedOption = singleDimensionOptions.find((opt) => opt.value === newDimension);
    if (selectedOption) {
      updateSelectValues(selectedOption, index);
    } else {
      // Clear selection if dimension not found
      updateSelectValues(null, index);
    }
  };

  const onCustomDimensionCreate = () => {
    // Prevent add if any selector is empty
    if (!selectValArr.every((val) => val && val.value)) {
      setIsConfigLoading(false);
      return;
    }
    setIsConfigLoading(true);
    const newDimensionCombination = getNewDimensionCombination(selectValArr, level);
    const aiAlertsConfig = aiConfigList.concat();
    const metrics = aiCohortDrawerConfig?.metrics || [];
    const insertIndex = getInsertIndex(aiAlertsConfig, level);
    aiAlertsConfig[level].dimensionData.splice(insertIndex, 0, newDimensionCombination);
    mutateAiAlertsConfig({ aiAlertsConfig, metrics });
    setPopoverOpen(false);
    onBlur();
  };

  // For dimension COMBINATIONS
  const getCombinationOptions = (inputIndex) => {
    const otherInputSelectedValue = selectValArr[inputIndex === 0 ? 1 : 0];
    if (!otherInputSelectedValue) {
      return [...preLevelDimensionOptions].sort((a, b) =>
        a.value?.toLowerCase().localeCompare(b.value?.toLowerCase()),
      );
    }
    const otherBackendNames = otherInputSelectedValue.backendName.split(BACKEND_NAME_SEPARATOR);
    return preLevelDimensionOptions
      .filter((option) => {
        if (!option.backendName) return true;
        const optionBackendNames = option.backendName.split(BACKEND_NAME_SEPARATOR);
        return optionBackendNames.every((name) => !otherBackendNames.includes(name));
      })
      .sort((a, b) => a.value?.toLowerCase().localeCompare(b.value?.toLowerCase()));
  };

  // For single DIMENSIONS
  const getDPIAvailableDimensions = (rawList, inputIndex) => {
    const otherSelectedBackendNames: string[] = [];
    selectValArr.forEach((val, idx) => {
      if (idx !== inputIndex && val?.backendName) {
        if (val.backendName.includes(BACKEND_NAME_SEPARATOR)) {
          otherSelectedBackendNames.push(...val.backendName.split(BACKEND_NAME_SEPARATOR));
        } else {
          otherSelectedBackendNames.push(val.backendName);
        }
      }
    });
    return rawList.filter(
      (option) => option.backendName && !otherSelectedBackendNames.includes(option.backendName),
    );
  };

  const getAvailableDimension = (rawList: any[], inputIndex: number): any[] => {
    let filtered: any[] = [];
    if (level > 1) {
      const otherInputSelectedValue = selectValArr[inputIndex === 0 ? 1 : 0];
      if (otherInputSelectedValue) {
        const selectCombinationBackendNameArr =
          otherInputSelectedValue.backendName.split(BACKEND_NAME_SEPARATOR);
        // The first-dropdown option (preLevelDimensionOptions) and the second-dropdown option
        // (singleDimensionOptions) can carry the same dimension under different backendName forms
        // (e.g. CDN vs CURRENT_CDN), so a backendName-only match misses and the dimension still
        // shows up in the second dropdown. Both lists resolve to the same display name (value),
        // so we also exclude by display name to reliably de-dup. (PUI-2936)
        const selectCombinationNameArr = getDimensionNameList(otherInputSelectedValue.value || '');
        filtered = rawList
          .filter((listOption) => {
            const listBackendArr = listOption.backendName.split(BACKEND_NAME_SEPARATOR);
            const backendOk = listBackendArr.every(
              (backendName) => !selectCombinationBackendNameArr.includes(backendName),
            );
            const nameOk = !selectCombinationNameArr.includes(listOption.value);
            return backendOk && nameOk;
          })
          .sort((a, b) => a.value?.toLowerCase().localeCompare(b.value?.toLowerCase()));
      }
    }
    if (!filtered.length) {
      filtered = [...rawList].sort((a, b) =>
        a.value?.toLowerCase().localeCompare(b.value?.toLowerCase()),
      );
    }
    // Ensure unique keys for each option
    return filtered.map((opt, idx) => ({
      ...opt,
      key: `${opt.backendName || opt.value || ''}__${idx}`,
    }));
  };

  // Component to render DimensionSelector with local state integration
  const renderSingleDimensionSelector = (index: number) => {
    const currentValue = selectValArr[index]?.value || '';

    return isAppModule ? (
      <div style={{ width: '100%' }}>
        <DimensionSelector
          dimension={currentValue}
          availableDimensions={getDPIAvailableDimensions(singleDimensionOptions, index)}
          disabled={false}
          onChange={onDimensionSelectorChange(index)}
        />
      </div>
    ) : (
      <StyledSelect
        showSearch
        value={selectValArr[index]}
        optionFilterProp="value"
        placeholder={`Select dimension`}
        onChange={onSelectChangeHandler(index)}
        options={getAvailableDimension(singleDimensionOptions, index)}
        filterOption={(input, option) =>
          (option?.value || '').toLowerCase().includes(input.toLowerCase())
        }
        allowClear
        autoFocus
        virtual={false}
        getPopupContainer={() => document.body}
      />
    );
  };

  // TODO use focus() in later phase
  const renderDimensionSelector = (level) => {
    if (level === 1) {
      return renderSingleDimensionSelector(0);
    } else {
      // level > 1
      return (
        <>
          <StyledSelect
            showSearch
            value={selectValArr[0]}
            optionFilterProp="children"
            placeholder={`Select dimension combination`}
            onChange={onSelectChangeHandler(0)}
            options={getCombinationOptions(0)}
            filterOption={(input, option) => getFilteredOptions(input, option)}
            allowClear
            autoFocus
            virtual={false}
            getPopupContainer={() => document.body}
          />
          {renderSingleDimensionSelector(1)}
        </>
      );
    }
  };

  const popupContent = (
    <>
      <DimensionTitle isPopup>{popupTitle}</DimensionTitle>
      <SelectWrapper>{renderDimensionSelector(level)}</SelectWrapper>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        disabled={disableCreate}
        onClick={onCustomDimensionCreate}
      >
        Add Dimension
      </Button>
    </>
  );

  return (
    <AddDimensionWrapper>
      <Popover
        placement="rightTop"
        trigger="click"
        content={popupContent}
        open={isPopoverOpen}
        onOpenChange={handlePopoverChange}
        getPopupContainer={() => document.body}
        styles={{ root: { width: 'max-content' } }}
        destroyTooltipOnHide={true}
      >
        <Tooltip title={isAdminUser ? popupTitle : 'Read Only'}>
          <AddDimensionBtn isAdminUser={isAdminUser}>+</AddDimensionBtn>
        </Tooltip>
      </Popover>
    </AddDimensionWrapper>
  );
}
